import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { 
  Truck, User, CheckCircle, XCircle, Clock, Package, 
  AlertCircle, ChevronDown, ChevronUp, Send,
  MapPin, Phone, Filter, Eye, RefreshCw, RotateCcw,
  ExternalLink, Copy, Building2, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusConfig = {
  pending: { label: 'Pending', color: 'text-yellow-400', bg: 'bg-yellow-500/10', icon: Clock },
  accepted: { label: 'Accepted', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: CheckCircle },
  picked: { label: 'Picked', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: Package },
  picked_up: { label: 'Picked Up', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: Package },
  on_the_way: { label: 'On The Way', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: Truck },
  in_transit: { label: 'In Transit', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Truck },
  out_for_delivery: { label: 'Out for Delivery', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: Truck },
  delivered: { label: 'Delivered', color: 'text-green-400', bg: 'bg-green-500/10', icon: CheckCircle },
  failed: { label: 'Failed', color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle },
  rto: { label: 'RTO', color: 'text-red-400', bg: 'bg-red-500/10', icon: RotateCcw },
  rto_initiated: { label: 'RTO Initiated', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: RotateCcw },
  rto_in_transit: { label: 'RTO In Transit', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: RotateCcw },
  rto_delivered: { label: 'RTO Delivered', color: 'text-gray-400', bg: 'bg-gray-500/10', icon: RotateCcw },
  booked: { label: 'Booked', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Package },
  manifested: { label: 'Booked', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Package },
  cancelled: { label: 'Cancelled', color: 'text-gray-400', bg: 'bg-gray-500/10', icon: XCircle },
  ndr: { label: 'NDR', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: AlertCircle },
};

// Shipment status groups — shared by stats calc & filter logic so the meaning
// of "RTO" / "NDR" / "In Transit" etc. stays consistent across both.
const RTO_STATUSES = ['rto', 'rto_initiated', 'rto_in_transit', 'rto_delivered'];
// `out_for_delivery` was previously rolled into IN_TRANSIT_STATUSES,
// which made the dedicated "Out for Delivery" card impossible to
// build. We split it out here. The In Transit card now ONLY counts
// pre-OFD movement; OFD has its own card.
const IN_TRANSIT_STATUSES = ['in_transit', 'picked', 'picked_up'];
const OUT_FOR_DELIVERY_STATUSES = ['out_for_delivery'];
const NDR_STATUSES = ['failed', 'ndr', 'undelivered', 'delivery_attempted'];

const courierConfig = {
  bluedart: { name: 'Bluedart', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  delhivery: { name: 'Delhivery', color: 'text-red-400', bg: 'bg-red-500/10' },
  dtdc: { name: 'DTDC', color: 'text-purple-400', bg: 'bg-purple-500/10' },
};

// --- Date-range filter helpers --------------------------------------------
// Operations on this page work in the merchant's *local* day boundary —
// "Today's deliveries" means today as the merchant sees it on the wall
// clock, not UTC. We normalise both range endpoints to local midnights
// (start = inclusive, end = exclusive) and then compare against the
// item's `created_at` (or whichever field the caller passes).
const startOfLocalDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const DATE_PRESETS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'mtd', label: 'Month to Date' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
];

// Returns {start, end} as Date objects (or null,null for "all"). `end`
// is EXCLUSIVE — i.e. for "Today" it's tomorrow's midnight so a row
// `created_at` at 23:59:59 today still falls inside the window.
const computeDateRange = (preset, customFrom, customTo) => {
  const now = new Date();
  const today = startOfLocalDay(now);
  switch (preset) {
    case 'today':
      return { start: today, end: addDays(today, 1) };
    case 'yesterday':
      return { start: addDays(today, -1), end: today };
    case 'this_week': {
      // Week starts Monday — most operations teams in IN/UAE think of
      // Mon as the first working day. Sunday = JS getDay()===0 → -6.
      const dow = today.getDay();
      const offset = dow === 0 ? -6 : 1 - dow;
      const start = addDays(today, offset);
      return { start, end: addDays(start, 7) };
    }
    case 'last_week': {
      const dow = today.getDay();
      const offset = dow === 0 ? -6 : 1 - dow;
      const startThisWeek = addDays(today, offset);
      return { start: addDays(startThisWeek, -7), end: startThisWeek };
    }
    case 'mtd':
      return {
        start: new Date(today.getFullYear(), today.getMonth(), 1),
        end: addDays(today, 1),
      };
    case 'last_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start, end };
    }
    case 'custom': {
      if (!customFrom && !customTo) return { start: null, end: null };
      const start = customFrom ? startOfLocalDay(new Date(customFrom)) : null;
      // End-exclusive: pick the day AFTER the chosen end so the user's
      // intent ("through Apr 27") includes everything on Apr 27.
      const end = customTo ? addDays(startOfLocalDay(new Date(customTo)), 1) : null;
      return { start, end };
    }
    case 'all':
    default:
      return { start: null, end: null };
  }
};

// Companion to computeDateRange — returns the "previous period"
// against which the current range should be compared. Logic:
//   today/yesterday        → the day immediately before
//   this_week/last_week    → the week immediately before
//   mtd                    → same number of days at the start of last
//                            month (Apr 1-22 vs Mar 1-22)
//   last_month             → the month before last
//   custom                 → same-length window ending at currStart-0
//   all                    → null,null (compare disabled)
//
// `currRange` is the range from computeDateRange for the same preset.
const computePreviousRange = (preset, currRange, customFrom, customTo) => {
  if (!currRange?.start || !currRange?.end) return { start: null, end: null };
  switch (preset) {
    case 'today':
    case 'yesterday':
      return { start: addDays(currRange.start, -1), end: currRange.start };
    case 'this_week':
    case 'last_week':
      return { start: addDays(currRange.start, -7), end: currRange.start };
    case 'mtd': {
      // currRange.start = 1st of this month, currRange.end = tomorrow.
      // Take the same number of days from the 1st of the previous month.
      const elapsedDays = Math.round((currRange.end - currRange.start) / 86400000);
      const prevMonthStart = new Date(
        currRange.start.getFullYear(),
        currRange.start.getMonth() - 1,
        1,
      );
      return { start: prevMonthStart, end: addDays(prevMonthStart, elapsedDays) };
    }
    case 'last_month': {
      // Previous = the month before last_month (i.e. two months ago).
      const start = new Date(
        currRange.start.getFullYear(),
        currRange.start.getMonth() - 1,
        1,
      );
      return { start, end: currRange.start };
    }
    case 'custom': {
      const len = currRange.end - currRange.start;
      return { start: new Date(currRange.start.getTime() - len), end: currRange.start };
    }
    case 'all':
    default:
      return { start: null, end: null };
  }
};

// Parse the item's date field. Tolerates ISO strings, Mongo
// ExtendedJSON, and missing values (treated as "out of range" so a
// stale row without `created_at` isn't accidentally matched).
const parseItemDate = (item, field) => {
  const raw = item?.[field] || item?.created_at || item?.booked_at;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const inDateRange = (item, range, field = 'created_at') => {
  if (!range.start && !range.end) return true; // "All Time"
  const d = parseItemDate(item, field);
  if (!d) return false;
  if (range.start && d < range.start) return false;
  if (range.end && d >= range.end) return false;
  return true;
};

// Reusable date-range filter component — the same control renders on
// both the Driver Deliveries and Courier Shipments tabs, with its own
// state lifted to the parent so each tab can have an independent
// selection.
const DateRangeFilter = ({ value, onChange, customFrom, customTo, onCustomChange, testIdPrefix }) => {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Calendar className="h-4 w-4 text-wt-text-muted" />
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          className="w-[170px] bg-wt-subtle border-wt-border text-wt-text-primary"
          data-testid={`${testIdPrefix}-date-preset`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-wt-paper border-wt-border">
          {DATE_PRESETS.map((p) => (
            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value === 'custom' && (
        <div className="flex items-center gap-2">
          <DateInput
            value={customFrom}
            onChange={(e) => onCustomChange({ from: e.target.value, to: customTo })}
            max={customTo || undefined}
            className="w-[160px] bg-wt-subtle border-wt-border text-wt-text-primary"
            data-testid={`${testIdPrefix}-date-from`}
          />
          <span className="text-wt-text-muted text-sm">to</span>
          <DateInput
            value={customTo}
            onChange={(e) => onCustomChange({ from: customFrom, to: e.target.value })}
            min={customFrom || undefined}
            className="w-[160px] bg-wt-subtle border-wt-border text-wt-text-primary"
            data-testid={`${testIdPrefix}-date-to`}
          />
        </div>
      )}
    </div>
  );
};

// Inline delta chip rendered next to KPI numbers. Shows ▲/▼ +/-N%
// (or absolute when prev = 0) coloured by whether the change is good
// for the merchant. `positiveIsBad` flips the colour mapping for
// metrics where higher = worse (Failed / NDR / RTO).
const DeltaChip = ({ current, previous, positiveIsBad = false, label }) => {
  if (current === 0 && previous === 0) return null;
  const diff = current - previous;
  if (diff === 0) {
    return (
      <span className="text-[10px] text-wt-text-muted ml-1" title={`${label || 'Previous'}: ${previous}`}>
        — vs {previous}
      </span>
    );
  }
  const goodDirection = positiveIsBad ? diff < 0 : diff > 0;
  const cls = goodDirection ? 'text-green-400' : 'text-red-400';
  const arrow = diff > 0 ? '▲' : '▼';
  const text = previous === 0
    ? `${arrow} ${Math.abs(diff)}`
    : `${arrow} ${Math.abs(Math.round((diff / previous) * 100))}%`;
  return (
    <span
      className={`text-[10px] font-medium ml-1 ${cls}`}
      title={`${label || 'Previous'}: ${previous}`}
      data-testid="kpi-delta-chip"
    >
      {text}
    </span>
  );
};

const DeliveriesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('drivers');
  
  // Driver deliveries state
  const [rawOrders, setRawOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [driverDateFilter, setDriverDateFilter] = useState('all');
  const [driverCustomRange, setDriverCustomRange] = useState({ from: '', to: '' });
  const [driverCompare, setDriverCompare] = useState(false);
  const [expandedDrivers, setExpandedDrivers] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Courier shipments state
  const [rawShipments, setRawShipments] = useState([]);
  const [courierFilter, setCourierFilter] = useState('all');
  const [courierStatusFilter, setCourierStatusFilter] = useState('all');
  const [courierDateFilter, setCourierDateFilter] = useState('all');
  const [courierCustomRange, setCourierCustomRange] = useState({ from: '', to: '' });
  const [courierCompare, setCourierCompare] = useState(false);
  const [expandedCouriers, setExpandedCouriers] = useState({});
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [shipmentDetailOpen, setShipmentDetailOpen] = useState(false);
  
  // NDR Action state
  const [ndrActionOpen, setNdrActionOpen] = useState(false);
  const [ndrAction, setNdrAction] = useState('reattempt');
  const [ndrReason, setNdrReason] = useState('');
  const [ndrDate, setNdrDate] = useState('');
  const [ndrProcessing, setNdrProcessing] = useState(false);
  
  // Sync tracking state
  const [syncingAll, setSyncingAll] = useState(false);
  // Live progress for the background "Sync All Tracking" job.
  // {done, total, succeeded, failed, status, failures[]}.
  const [syncProgress, setSyncProgress] = useState(null);
  const [syncingShipment, setSyncingShipment] = useState(null);

  useEffect(() => {
    loadDeliveryData();
  }, []);

  // Loader is intentionally lean — it ONLY fetches and stores raw
  // arrays. All grouping, deduping, stats, and filtering happens in
  // useMemo blocks below so the date / status / courier filters can
  // recompute every derived view instantly without a network round trip.
  const loadDeliveryData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const ordersRes = await axios.get(`${API_BASE_URL}/orders?limit=500`, { headers });
      const orders = ordersRes.data.orders || [];

      let courierShipmentsFromAPI = [];
      try {
        const shipmentsRes = await axios.get(`${API_BASE_URL}/v1/couriers/shipments?limit=200`, { headers });
        courierShipmentsFromAPI = shipmentsRes.data.shipments || [];
      } catch (err) {
        console.error('Failed to fetch courier shipments:', err);
      }

      // Merge orders + courier_shipments collection into a single
      // de-duplicated list (keyed by AWB). Same logic as before — just
      // moved out of the previous monolithic loader so it runs once.
      const courierShipmentsList = [];
      const processedAwbs = new Set();

      courierShipmentsFromAPI.forEach((shipment) => {
        if (shipment.awb_number) {
          processedAwbs.add(shipment.awb_number);
          const matchingOrder = orders.find((o) => o.id === shipment.order_id);
          courierShipmentsList.push({
            ...shipment,
            shipment_id: shipment.id,
            order_number: shipment.order_number || matchingOrder?.order_number,
            customer_name: matchingOrder?.customer_name || shipment.consignee_name,
            customer_phone: matchingOrder?.customer_phone || shipment.consignee_phone,
            shipping_address: matchingOrder?.shipping_address_full || matchingOrder?.shipping_address || shipment.delivery_address,
            total_amount: matchingOrder?.total_amount,
            payment_status: matchingOrder?.payment_status,
            courier_provider: shipment.courier_type,
            // Date filter prefers booked_at when available (closer to
            // operational meaning) else falls back to created_at.
            created_at: shipment.created_at || matchingOrder?.created_at,
          });
        }
      });

      orders.forEach((order) => {
        // Embedded shipments on split orders.
        if (order.shipments && order.shipments.length > 0) {
          order.shipments.forEach((shipment) => {
            if (shipment.awb_number && shipment.courier_provider && !processedAwbs.has(shipment.awb_number)) {
              processedAwbs.add(shipment.awb_number);
              courierShipmentsList.push({
                ...shipment,
                order_id: order.id,
                order_number: order.order_number,
                customer_name: order.customer_name,
                customer_phone: order.customer_phone,
                shipping_address: order.shipping_address_full || order.shipping_address,
                total_amount: order.total_amount,
                payment_status: order.payment_status,
                created_at: shipment.created_at || order.created_at,
              });
            }
          });
        }
        // Order-level courier bookings on non-split orders.
        if (order.awb_number && order.courier_type && !processedAwbs.has(order.awb_number)) {
          processedAwbs.add(order.awb_number);
          courierShipmentsList.push({
            order_id: order.id,
            order_number: order.order_number,
            awb_number: order.awb_number,
            courier_provider: order.courier_type,
            status: order.courier_status || 'booked',
            customer_name: order.customer_name,
            customer_phone: order.customer_phone,
            shipping_address: order.shipping_address_full || order.shipping_address,
            total_amount: order.total_amount,
            payment_status: order.payment_status,
            booked_at: order.courier_booked_at,
            created_at: order.courier_booked_at || order.created_at,
          });
        }
      });

      setRawOrders(orders);
      setRawShipments(courierShipmentsList);
    } catch (error) {
      toast.error('Failed to load delivery data');
    } finally {
      setLoading(false);
    }
  };

  const toggleDriverExpand = (driverId) => {
    setExpandedDrivers(prev => ({ ...prev, [driverId]: !prev[driverId] }));
  };

  const toggleCourierExpand = (courier) => {
    setExpandedCouriers(prev => ({ ...prev, [courier]: !prev[courier] }));
  };

  // --- Derived data: driver tab --------------------------------------
  // Date-range first → stats + grouping flow from the *visible* set so
  // KPI tiles match the cards below them. Status filter applies after
  // (it's interactive — the date filter is the bigger "scope" knob).
  const driverDateRange = useMemo(
    () => computeDateRange(driverDateFilter, driverCustomRange.from, driverCustomRange.to),
    [driverDateFilter, driverCustomRange.from, driverCustomRange.to]
  );

  const dateScopedOrders = useMemo(
    () => rawOrders.filter((o) => inDateRange(o, driverDateRange, 'created_at')),
    [rawOrders, driverDateRange]
  );

  const stats = useMemo(() => ({
    total: dateScopedOrders.length,
    pending: dateScopedOrders.filter((o) => o.fulfillment_status === 'pending').length,
    in_progress: dateScopedOrders.filter((o) => ['accepted', 'picked', 'on_the_way'].includes(o.fulfillment_status)).length,
    delivered: dateScopedOrders.filter((o) => o.fulfillment_status === 'delivered').length,
    failed: dateScopedOrders.filter((o) => o.fulfillment_status === 'failed').length,
  }), [dateScopedOrders]);

  // Previous-period driver stats (only computed when comparison toggle
  // is on AND a finite preset is selected — "All Time" has no prior).
  const prevDriverRange = useMemo(
    () => (driverCompare ? computePreviousRange(driverDateFilter, driverDateRange) : { start: null, end: null }),
    [driverCompare, driverDateFilter, driverDateRange]
  );
  const prevStats = useMemo(() => {
    if (!prevDriverRange.start) return null;
    const slice = rawOrders.filter((o) => inDateRange(o, prevDriverRange, 'created_at'));
    return {
      total: slice.length,
      pending: slice.filter((o) => o.fulfillment_status === 'pending').length,
      in_progress: slice.filter((o) => ['accepted', 'picked', 'on_the_way'].includes(o.fulfillment_status)).length,
      delivered: slice.filter((o) => o.fulfillment_status === 'delivered').length,
      failed: slice.filter((o) => o.fulfillment_status === 'failed').length,
    };
  }, [rawOrders, prevDriverRange]);

  const { driverDeliveries, unassignedOrders } = useMemo(() => {
    const driverMap = {};
    const unassigned = [];
    dateScopedOrders.forEach((order) => {
      if (order.assigned_driver_id) {
        if (!driverMap[order.assigned_driver_id]) {
          driverMap[order.assigned_driver_id] = {
            driver_id: order.assigned_driver_id,
            driver_name: order.assigned_driver_name || 'Unknown Driver',
            orders: [],
            stats: { pending: 0, in_progress: 0, delivered: 0, failed: 0 },
          };
        }
        driverMap[order.assigned_driver_id].orders.push(order);
        if (order.fulfillment_status === 'pending') driverMap[order.assigned_driver_id].stats.pending++;
        else if (['accepted', 'picked', 'on_the_way'].includes(order.fulfillment_status)) driverMap[order.assigned_driver_id].stats.in_progress++;
        else if (order.fulfillment_status === 'delivered') driverMap[order.assigned_driver_id].stats.delivered++;
        else if (order.fulfillment_status === 'failed') driverMap[order.assigned_driver_id].stats.failed++;
      } else {
        const needsDelivery = !['delivered', 'fulfilled', 'cancelled', 'refunded'].includes(order.fulfillment_status);
        if (needsDelivery) unassigned.push(order);
      }
    });
    return { driverDeliveries: Object.values(driverMap), unassignedOrders: unassigned };
  }, [dateScopedOrders]);

  // --- Derived data: courier tab -------------------------------------
  const courierDateRange = useMemo(
    () => computeDateRange(courierDateFilter, courierCustomRange.from, courierCustomRange.to),
    [courierDateFilter, courierCustomRange.from, courierCustomRange.to]
  );

  const dateScopedShipments = useMemo(
    () => rawShipments.filter((s) => inDateRange(s, courierDateRange, 'created_at')),
    [rawShipments, courierDateRange]
  );

  const courierShipments = dateScopedShipments;

  const courierStats = useMemo(() => {
    const isRto = (s) => RTO_STATUSES.includes(s.status);
    const isNdr = (s) => NDR_STATUSES.includes(s.status) || s.ndr_status;
    const isInTransit = (s) => IN_TRANSIT_STATUSES.includes(s.status);
    const isOfd = (s) => OUT_FOR_DELIVERY_STATUSES.includes(s.status);
    return {
      total: dateScopedShipments.length,
      booked: dateScopedShipments.filter((s) => ['booked', 'manifested'].includes(s.status)).length,
      in_transit: dateScopedShipments.filter(isInTransit).length,
      out_for_delivery: dateScopedShipments.filter(isOfd).length,
      delivered: dateScopedShipments.filter((s) => s.status === 'delivered').length,
      failed: dateScopedShipments.filter(isNdr).length,
      rto: dateScopedShipments.filter(isRto).length,
      rto_initiated: dateScopedShipments.filter((s) => ['rto', 'rto_initiated'].includes(s.status)).length,
      rto_in_transit: dateScopedShipments.filter((s) => s.status === 'rto_in_transit').length,
      rto_delivered: dateScopedShipments.filter((s) => s.status === 'rto_delivered').length,
      cancelled: dateScopedShipments.filter((s) => s.status === 'cancelled').length,
      ndr: dateScopedShipments.filter(isNdr).length,
    };
  }, [dateScopedShipments]);

  // Previous-period courier stats (mirrors driver-side comparison).
  const prevCourierRange = useMemo(
    () => (courierCompare ? computePreviousRange(courierDateFilter, courierDateRange) : { start: null, end: null }),
    [courierCompare, courierDateFilter, courierDateRange]
  );
  const prevCourierStats = useMemo(() => {
    if (!prevCourierRange.start) return null;
    const isRto = (s) => RTO_STATUSES.includes(s.status);
    const isNdr = (s) => NDR_STATUSES.includes(s.status) || s.ndr_status;
    const isInTransit = (s) => IN_TRANSIT_STATUSES.includes(s.status);
    const isOfd = (s) => OUT_FOR_DELIVERY_STATUSES.includes(s.status);
    const slice = rawShipments.filter((s) => inDateRange(s, prevCourierRange, 'created_at'));
    return {
      total: slice.length,
      booked: slice.filter((s) => ['booked', 'manifested'].includes(s.status)).length,
      in_transit: slice.filter(isInTransit).length,
      out_for_delivery: slice.filter(isOfd).length,
      delivered: slice.filter((s) => s.status === 'delivered').length,
      failed: slice.filter(isNdr).length,
      rto_initiated: slice.filter((s) => ['rto', 'rto_initiated'].includes(s.status)).length,
      rto_in_transit: slice.filter((s) => s.status === 'rto_in_transit').length,
      rto_delivered: slice.filter((s) => s.status === 'rto_delivered').length,
      ndr: slice.filter(isNdr).length,
      rto: slice.filter(isRto).length,
    };
  }, [rawShipments, prevCourierRange]);

  const filterOrders = (orders) => {
    if (statusFilter === 'all') return orders;
    if (statusFilter === 'in_progress') {
      return orders.filter(o => ['accepted', 'picked', 'on_the_way'].includes(o.fulfillment_status));
    }
    return orders.filter(o => o.fulfillment_status === statusFilter);
  };

  const filterCourierShipments = (shipments) => {
    let filtered = shipments;
    if (courierFilter !== 'all') {
      filtered = filtered.filter(s => s.courier_provider === courierFilter);
    }
    if (courierStatusFilter !== 'all') {
      if (courierStatusFilter === 'in_transit') {
        filtered = filtered.filter(s => IN_TRANSIT_STATUSES.includes(s.status));
      } else if (courierStatusFilter === 'out_for_delivery') {
        filtered = filtered.filter(s => OUT_FOR_DELIVERY_STATUSES.includes(s.status));
      } else if (courierStatusFilter === 'ndr') {
        filtered = filtered.filter(s => NDR_STATUSES.includes(s.status) || s.ndr_status);
      } else if (courierStatusFilter === 'rto') {
        filtered = filtered.filter(s => RTO_STATUSES.includes(s.status));
      } else if (courierStatusFilter === 'booked') {
        filtered = filtered.filter(s => ['booked', 'manifested'].includes(s.status));
      } else {
        filtered = filtered.filter(s => s.status === courierStatusFilter);
      }
    }
    return filtered;
  };

  // Group shipments by courier
  const groupedByCourier = () => {
    const groups = {};
    filterCourierShipments(courierShipments).forEach(shipment => {
      const courier = shipment.courier_provider || 'unknown';
      if (!groups[courier]) {
        groups[courier] = {
          courier,
          shipments: [],
          stats: {
            booked: 0,
            in_transit: 0,
            out_for_delivery: 0,
            delivered: 0,
            failed: 0,
            rto: 0,
            rto_initiated: 0,
            rto_in_transit: 0,
            rto_delivered: 0,
            cancelled: 0,
            ndr: 0,
          },
        };
      }
      groups[courier].shipments.push(shipment);

      const st = shipment.status;
      if (['booked', 'manifested'].includes(st)) groups[courier].stats.booked++;
      else if (IN_TRANSIT_STATUSES.includes(st)) groups[courier].stats.in_transit++;
      else if (OUT_FOR_DELIVERY_STATUSES.includes(st)) groups[courier].stats.out_for_delivery++;
      else if (st === 'delivered') groups[courier].stats.delivered++;
      else if (NDR_STATUSES.includes(st)) groups[courier].stats.failed++;
      else if (RTO_STATUSES.includes(st)) groups[courier].stats.rto++;
      else if (st === 'cancelled') groups[courier].stats.cancelled++;
      // RTO sub-state breakdown — these are also counted in `rto`
      // above; the sub-counters let the courier-card surface RTO
      // Init / RTO IT / RTO Dlv individually.
      if (['rto', 'rto_initiated'].includes(st)) groups[courier].stats.rto_initiated++;
      if (st === 'rto_in_transit') groups[courier].stats.rto_in_transit++;
      if (st === 'rto_delivered') groups[courier].stats.rto_delivered++;
      if (NDR_STATUSES.includes(st) || shipment.ndr_status) groups[courier].stats.ndr++;
    });
    return Object.values(groups);
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setDetailDialogOpen(true);
  };

  const viewShipmentDetails = (shipment) => {
    setSelectedShipment(shipment);
    setShipmentDetailOpen(true);
  };

  const openNdrAction = (shipment) => {
    setSelectedShipment(shipment);
    setNdrAction('reattempt');
    setNdrReason('');
    setNdrDate(new Date().toISOString().split('T')[0]);
    setNdrActionOpen(true);
  };

  const handleNdrAction = async () => {
    if (!selectedShipment) return;
    
    setNdrProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = ndrAction === 'reattempt' 
        ? `${API_BASE_URL}/v1/couriers/ndr/reattempt`
        : `${API_BASE_URL}/v1/couriers/ndr/rto`;
      
      await axios.post(endpoint, {
        order_id: selectedShipment.order_id,
        shipment_number: selectedShipment.shipment_number,
        awb: selectedShipment.awb_number,
        courier_type: selectedShipment.courier_provider,
        reason: ndrReason,
        reattempt_date: ndrAction === 'reattempt' ? ndrDate : undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${ndrAction === 'reattempt' ? 'Reattempt' : 'RTO'} request submitted successfully`);
      setNdrActionOpen(false);
      loadDeliveryData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to submit ${ndrAction} request`);
    } finally {
      setNdrProcessing(false);
    }
  };

  const trackShipment = async (shipment) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/couriers/track/${shipment.awb_number}?courier_type=${shipment.courier_provider}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Status: ${response.data.status}`);
        // Refresh data to get updated status
        loadDeliveryData();
      }
    } catch (error) {
      toast.error('Failed to track shipment');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Sync all tracking — kicks off a background job and polls for
  // progress. The synchronous /sync-all endpoint was timing out at
  // the gateway when the merchant had 300+ active shipments
  // (Bluedart + DTDC + Delhivery combined), even though many had
  // already synced on the server. The new flow returns a job_id
  // immediately and polls every 2s so the UI can show smooth
  // progress instead of staring at a spinner that eventually fails.
  const syncAllTracking = async () => {
    setSyncingAll(true);
    setSyncProgress({ done: 0, total: 0, succeeded: 0, failed: 0, status: 'starting' });
    try {
      const token = localStorage.getItem('token');
      const startResp = await axios.post(
        `${API_BASE_URL}/v1/couriers/tracking/sync-all/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { job_id, total } = startResp.data;
      if (!job_id) {
        // Server returned the legacy synchronous shape — render that
        // and bail out (nothing to poll).
        const { updated, failed, total: legacyTotal } =
          startResp.data.summary || startResp.data;
        toast.success(`Tracking synced: ${updated} updated, ${failed} failed of ${legacyTotal} total`);
        loadDeliveryData();
        return;
      }
      setSyncProgress({ done: 0, total, succeeded: 0, failed: 0, status: 'running', jobId: job_id });

      // Poll until status === completed. Each poll is cheap (a
      // single doc read). We cap at 5 minutes to avoid runaway
      // polling if the worker dies — the user can hit Sync again.
      const maxPolls = 150; // 150 * 2s = 5 min
      let polls = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        polls += 1;
        if (polls > maxPolls) {
          toast.error('Sync is taking longer than expected — refresh to see latest counts');
          break;
        }
        await new Promise((r) => setTimeout(r, 2000));
        const statusResp = await axios.get(
          `${API_BASE_URL}/v1/couriers/tracking/sync-all/status/${job_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const job = statusResp.data || {};
        setSyncProgress({
          jobId: job_id,
          done: job.done || 0,
          total: job.total || total,
          succeeded: job.succeeded || 0,
          failed: job.failed || 0,
          status: job.status || 'running',
          failures: job.failures || [],
        });
        if (job.status === 'completed' || job.status === 'failed') {
          if (job.status === 'completed') {
            toast.success(`Sync done — ${job.succeeded} updated, ${job.failed} failed of ${job.total}`);
          } else {
            toast.error('Sync job failed — please retry');
          }
          loadDeliveryData();
          break;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync tracking');
    } finally {
      setSyncingAll(false);
    }
  };

  // Sync single shipment tracking
  const syncSingleTracking = async (shipment) => {
    setSyncingShipment(shipment.awb_number);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/v1/couriers/tracking/sync-single`,
        {
          awb_number: shipment.awb_number,
          courier_type: shipment.courier_provider,
          order_id: shipment.order_id,
          shipment_number: shipment.shipment_number
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Status: ${response.data.status}`);
        loadDeliveryData(); // Refresh data
      } else {
        toast.error(response.data.error || 'Tracking sync failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync tracking');
    } finally {
      setSyncingShipment(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-playfair text-wt-text-primary">
            Delivery Management
          </h1>
          <p className="text-wt-text-muted text-sm mt-1">
            Track and manage deliveries by drivers and courier partners
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button onClick={loadDeliveryData} variant="outline" size="sm" className="border-wt-border text-wt-text-primary">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {activeTab === 'couriers' && (
            <Button 
              onClick={syncAllTracking} 
              disabled={syncingAll}
              variant="outline" 
              size="sm" 
              className="border-wt-gold/50 text-wt-gold hover:bg-wt-gold/10"
              data-testid="sync-all-tracking-btn"
            >
              {syncingAll ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {syncingAll ? 'Syncing...' : 'Sync All Tracking'}
            </Button>
          )}
          <Button onClick={() => navigate('/orders')} variant="outline" className="border-wt-border text-wt-text-primary">
            View Orders
          </Button>
        </div>
      </div>

      {/* Sync All Tracking — live progress. Renders only while a
          job is running OR for ~3s after completion so the operator
          gets a final "X of Y synced" snapshot before it disappears. */}
      {syncProgress && (syncProgress.status === 'running' || syncingAll) && (
        <div
          className="bg-wt-paper border border-wt-gold/40 rounded-xl p-4 space-y-2"
          data-testid="sync-progress-bar"
        >
          <div className="flex items-center justify-between text-sm">
            <span className="text-wt-text-primary font-medium">
              Syncing tracking… {syncProgress.done}/{syncProgress.total || '?'}
            </span>
            <span className="text-wt-text-muted">
              <span className="text-green-400">{syncProgress.succeeded} ok</span>
              {' · '}
              <span className="text-red-400">{syncProgress.failed} failed</span>
            </span>
          </div>
          <div className="h-2 bg-wt-subtle rounded-full overflow-hidden">
            <div
              className="h-full bg-wt-gold transition-all duration-500"
              style={{
                width: syncProgress.total
                  ? `${Math.min(100, Math.round((syncProgress.done / syncProgress.total) * 100))}%`
                  : '5%',
              }}
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-wt-subtle border border-wt-border">
          <TabsTrigger 
            value="drivers" 
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-black"
          >
            <User className="h-4 w-4 mr-2" />
            Driver Deliveries
          </TabsTrigger>
          <TabsTrigger 
            value="couriers" 
            className="data-[state=active]:bg-wt-gold data-[state=active]:text-black"
          >
            <Building2 className="h-4 w-4 mr-2" />
            Courier Shipments
          </TabsTrigger>
        </TabsList>

        {/* Driver Deliveries Tab */}
        <TabsContent value="drivers" className="space-y-6">
          {/* Driver Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Package className="h-4 w-4 text-wt-gold" />
                <span className="text-xs text-wt-text-muted">Total Orders</span>
              </div>
              <p className="text-2xl font-bold text-wt-text-primary">
                {stats.total}
                {prevStats && <DeltaChip current={stats.total} previous={prevStats.total} label="Prev period total" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-yellow-400" />
                <span className="text-xs text-wt-text-muted">Pending</span>
              </div>
              <p className="text-2xl font-bold text-yellow-400">
                {stats.pending}
                {prevStats && <DeltaChip current={stats.pending} previous={prevStats.pending} positiveIsBad label="Prev period pending" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="h-4 w-4 text-orange-400" />
                <span className="text-xs text-wt-text-muted">In Progress</span>
              </div>
              <p className="text-2xl font-bold text-orange-400">
                {stats.in_progress}
                {prevStats && <DeltaChip current={stats.in_progress} previous={prevStats.in_progress} label="Prev period in progress" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <span className="text-xs text-wt-text-muted">Delivered</span>
              </div>
              <p className="text-2xl font-bold text-green-400">
                {stats.delivered}
                {prevStats && <DeltaChip current={stats.delivered} previous={prevStats.delivered} label="Prev period delivered" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="h-4 w-4 text-red-400" />
                <span className="text-xs text-wt-text-muted">Failed</span>
              </div>
              <p className="text-2xl font-bold text-red-400">
                {stats.failed}
                {prevStats && <DeltaChip current={stats.failed} previous={prevStats.failed} positiveIsBad label="Prev period failed" />}
              </p>
            </motion.div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <DateRangeFilter
              value={driverDateFilter}
              onChange={(v) => setDriverDateFilter(v)}
              customFrom={driverCustomRange.from}
              customTo={driverCustomRange.to}
              onCustomChange={setDriverCustomRange}
              testIdPrefix="driver"
            />
            {driverDateFilter !== 'all' && (
              <Button
                variant={driverCompare ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDriverCompare((v) => !v)}
                className={driverCompare
                  ? 'bg-wt-gold text-black hover:bg-wt-gold/90'
                  : 'border-wt-border text-wt-text-muted hover:text-wt-text-primary'}
                data-testid="driver-compare-toggle"
              >
                Compare prev. period
              </Button>
            )}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-wt-text-muted" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger
                  className="w-[150px] bg-wt-subtle border-wt-border text-wt-text-primary"
                  data-testid="driver-status-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Unassigned Orders Warning */}
          {unassignedOrders.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-6 w-6 text-orange-400" />
                  <div>
                    <h3 className="font-medium text-orange-400">{unassignedOrders.length} Unassigned Orders</h3>
                    <p className="text-sm text-wt-text-muted">These orders need to be assigned to drivers</p>
                  </div>
                </div>
                <Button onClick={() => navigate('/orders')} className="bg-orange-500 hover:bg-orange-600 text-white">
                  Assign Drivers
                </Button>
              </div>
            </motion.div>
          )}

          {/* Driver Cards */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-wt-text-primary flex items-center gap-2">
              <User className="h-5 w-5 text-wt-gold" />
              Deliveries by Driver
            </h2>

            {driverDeliveries.length === 0 ? (
              <div className="text-center py-12 bg-wt-paper border border-wt-border rounded-xl">
                <Truck className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-50" />
                <p className="text-wt-text-muted">No deliveries assigned to drivers yet</p>
              </div>
            ) : (
              driverDeliveries.map((driver, idx) => {
                const filteredOrders = filterOrders(driver.orders);
                const isExpanded = expandedDrivers[driver.driver_id];

                return (
                  <motion.div key={driver.driver_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
                    <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-wt-subtle smooth-transition" onClick={() => toggleDriverExpand(driver.driver_id)}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-wt-gold/20 flex items-center justify-center">
                          <User className="h-6 w-6 text-wt-gold" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-wt-text-primary">{driver.driver_name}</h3>
                          <p className="text-sm text-wt-text-muted">{driver.orders.length} total orders</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="hidden md:flex items-center gap-4">
                          <div className="text-center"><p className="text-lg font-bold text-yellow-400">{driver.stats.pending}</p><p className="text-xs text-wt-text-muted">Pending</p></div>
                          <div className="text-center"><p className="text-lg font-bold text-orange-400">{driver.stats.in_progress}</p><p className="text-xs text-wt-text-muted">In Progress</p></div>
                          <div className="text-center"><p className="text-lg font-bold text-green-400">{driver.stats.delivered}</p><p className="text-xs text-wt-text-muted">Delivered</p></div>
                          <div className="text-center"><p className="text-lg font-bold text-red-400">{driver.stats.failed}</p><p className="text-xs text-wt-text-muted">Failed</p></div>
                        </div>
                        {isExpanded ? <ChevronUp className="h-5 w-5 text-wt-text-muted" /> : <ChevronDown className="h-5 w-5 text-wt-text-muted" />}
                      </div>
                    </div>

                    {isExpanded && filteredOrders.length > 0 && (
                      <div className="border-t border-wt-border p-4 space-y-3">
                        {filteredOrders.map(order => {
                          const statusCfg = statusConfig[order.fulfillment_status] || statusConfig.pending;
                          const StatusIcon = statusCfg.icon;
                          return (
                            <div key={order.id} className="flex items-center justify-between p-3 bg-wt-subtle rounded-lg hover:bg-wt-border smooth-transition cursor-pointer" onClick={() => viewOrderDetails(order)}>
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${statusCfg.bg}`}><StatusIcon className={`h-4 w-4 ${statusCfg.color}`} /></div>
                                <div>
                                  <p className="text-wt-text-primary font-medium">{order.order_number}</p>
                                  <p className="text-sm text-wt-text-muted">{order.customer_name}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.color}`}>{statusCfg.label}</span>
                                <Eye className="h-4 w-4 text-wt-text-muted" />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Courier Shipments Tab */}
        <TabsContent value="couriers" className="space-y-6">
          {/* Courier Stats — ordered by lifecycle: Total → Booked → In
              Transit → OFD → Delivered → NDR → RTO substates → Failed.
              The user reported that OFD / RTO Initiated / RTO In
              Transit / RTO Delivered counts were missing — they're
              now first-class cards. */}
          <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-9 gap-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Package className="h-4 w-4 text-wt-gold" />
                <span className="text-xs text-wt-text-muted">Total Shipments</span>
              </div>
              <p className="text-2xl font-bold text-wt-text-primary">
                {courierStats.total}
                {prevCourierStats && <DeltaChip current={courierStats.total} previous={prevCourierStats.total} label="Prev period total" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Package className="h-4 w-4 text-blue-400" />
                <span className="text-xs text-wt-text-muted">Booked</span>
              </div>
              <p className="text-2xl font-bold text-blue-400">
                {courierStats.booked}
                {prevCourierStats && <DeltaChip current={courierStats.booked} previous={prevCourierStats.booked} label="Prev period booked" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="h-4 w-4 text-orange-400" />
                <span className="text-xs text-wt-text-muted">In Transit</span>
              </div>
              <p className="text-2xl font-bold text-orange-400">
                {courierStats.in_transit}
                {prevCourierStats && <DeltaChip current={courierStats.in_transit} previous={prevCourierStats.in_transit} label="Prev period in transit" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.13 }} className="bg-wt-paper border border-wt-border rounded-xl p-4" data-testid="courier-stat-out-for-delivery">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="h-4 w-4 text-yellow-400" />
                <span className="text-xs text-wt-text-muted">Out for Delivery</span>
              </div>
              <p className="text-2xl font-bold text-yellow-400">
                {courierStats.out_for_delivery}
                {prevCourierStats && <DeltaChip current={courierStats.out_for_delivery} previous={prevCourierStats.out_for_delivery} label="Prev period OFD" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <span className="text-xs text-wt-text-muted">Delivered</span>
              </div>
              <p className="text-2xl font-bold text-green-400">
                {courierStats.delivered}
                {prevCourierStats && <DeltaChip current={courierStats.delivered} previous={prevCourierStats.delivered} label="Prev period delivered" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-wt-paper border border-wt-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-orange-400" />
                <span className="text-xs text-wt-text-muted">NDR</span>
              </div>
              <p className="text-2xl font-bold text-orange-400">
                {courierStats.ndr}
                {prevCourierStats && <DeltaChip current={courierStats.ndr} previous={prevCourierStats.ndr} positiveIsBad label="Prev period NDR" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }} className="bg-wt-paper border border-wt-border rounded-xl p-4" data-testid="courier-stat-rto-initiated">
              <div className="flex items-center gap-2 mb-2">
                <RotateCcw className="h-4 w-4 text-orange-300" />
                <span className="text-xs text-wt-text-muted">RTO Initiated</span>
              </div>
              <p className="text-2xl font-bold text-orange-300">
                {courierStats.rto_initiated}
                {prevCourierStats && <DeltaChip current={courierStats.rto_initiated} previous={prevCourierStats.rto_initiated} positiveIsBad label="Prev period RTO Initiated" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }} className="bg-wt-paper border border-wt-border rounded-xl p-4" data-testid="courier-stat-rto-in-transit">
              <div className="flex items-center gap-2 mb-2">
                <RotateCcw className="h-4 w-4 text-orange-400" />
                <span className="text-xs text-wt-text-muted">RTO In Transit</span>
              </div>
              <p className="text-2xl font-bold text-orange-400">
                {courierStats.rto_in_transit}
                {prevCourierStats && <DeltaChip current={courierStats.rto_in_transit} previous={prevCourierStats.rto_in_transit} positiveIsBad label="Prev period RTO In Transit" />}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26 }} className="bg-wt-paper border border-wt-border rounded-xl p-4" data-testid="courier-stat-rto-delivered">
              <div className="flex items-center gap-2 mb-2">
                <RotateCcw className="h-4 w-4 text-red-400" />
                <span className="text-xs text-wt-text-muted">RTO Delivered</span>
              </div>
              <p className="text-2xl font-bold text-red-400">
                {courierStats.rto_delivered}
                {prevCourierStats && <DeltaChip current={courierStats.rto_delivered} previous={prevCourierStats.rto_delivered} positiveIsBad label="Prev period RTO Delivered" />}
              </p>
            </motion.div>
          </div>

          {/* Courier Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <DateRangeFilter
              value={courierDateFilter}
              onChange={(v) => setCourierDateFilter(v)}
              customFrom={courierCustomRange.from}
              customTo={courierCustomRange.to}
              onCustomChange={setCourierCustomRange}
              testIdPrefix="courier"
            />
            {courierDateFilter !== 'all' && (
              <Button
                variant={courierCompare ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCourierCompare((v) => !v)}
                className={courierCompare
                  ? 'bg-wt-gold text-black hover:bg-wt-gold/90'
                  : 'border-wt-border text-wt-text-muted hover:text-wt-text-primary'}
                data-testid="courier-compare-toggle"
              >
                Compare prev. period
              </Button>
            )}
            <Filter className="h-4 w-4 text-wt-text-muted" />
            <Select value={courierFilter} onValueChange={setCourierFilter}>
              <SelectTrigger className="w-[150px] bg-wt-subtle border-wt-border text-wt-text-primary">
                <SelectValue placeholder="All Couriers" />
              </SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border">
                <SelectItem value="all">All Couriers</SelectItem>
                <SelectItem value="bluedart">Bluedart</SelectItem>
                <SelectItem value="dtdc">DTDC</SelectItem>
                <SelectItem value="delhivery">Delhivery</SelectItem>
              </SelectContent>
            </Select>
            <Select value={courierStatusFilter} onValueChange={setCourierStatusFilter}>
              <SelectTrigger className="w-[150px] bg-wt-subtle border-wt-border text-wt-text-primary">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="booked">Booked</SelectItem>
                <SelectItem value="in_transit">In Transit</SelectItem>
                <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="ndr">NDR (Failed Delivery)</SelectItem>
                <SelectItem value="rto">RTO (All stages)</SelectItem>
                <SelectItem value="rto_initiated">RTO Initiated</SelectItem>
                <SelectItem value="rto_in_transit">RTO In Transit</SelectItem>
                <SelectItem value="rto_delivered">RTO Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Courier Cards */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-wt-text-primary flex items-center gap-2">
              <Building2 className="h-5 w-5 text-wt-gold" />
              Shipments by Courier
            </h2>

            {groupedByCourier().length === 0 ? (
              <div className="text-center py-12 bg-wt-paper border border-wt-border rounded-xl">
                <Package className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-50" />
                <p className="text-wt-text-muted">No courier shipments found</p>
                <p className="text-sm text-wt-text-muted mt-1">Book shipments from the Orders page</p>
              </div>
            ) : (
              groupedByCourier().map((group, idx) => {
                const config = courierConfig[group.courier] || { name: group.courier, color: 'text-gray-400', bg: 'bg-gray-500/10' };
                const isExpanded = expandedCouriers[group.courier];

                return (
                  <motion.div key={group.courier} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
                    <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-wt-subtle smooth-transition" onClick={() => toggleCourierExpand(group.courier)}>
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full ${config.bg} flex items-center justify-center`}>
                          <Building2 className={`h-6 w-6 ${config.color}`} />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-wt-text-primary">{config.name}</h3>
                          <p className="text-sm text-wt-text-muted">{group.shipments.length} shipments</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="hidden md:flex items-center gap-4">
                          <div className="text-center"><p className="text-lg font-bold text-blue-400">{group.stats.booked}</p><p className="text-xs text-wt-text-muted">Booked</p></div>
                          <div className="text-center"><p className="text-lg font-bold text-orange-400">{group.stats.in_transit}</p><p className="text-xs text-wt-text-muted">In Transit</p></div>
                          {group.stats.out_for_delivery > 0 && <div className="text-center"><p className="text-lg font-bold text-yellow-400">{group.stats.out_for_delivery}</p><p className="text-xs text-wt-text-muted">OFD</p></div>}
                          <div className="text-center"><p className="text-lg font-bold text-green-400">{group.stats.delivered}</p><p className="text-xs text-wt-text-muted">Delivered</p></div>
                          {group.stats.ndr > 0 && <div className="text-center"><p className="text-lg font-bold text-orange-400">{group.stats.ndr}</p><p className="text-xs text-wt-text-muted">NDR</p></div>}
                          {group.stats.rto_initiated > 0 && <div className="text-center"><p className="text-lg font-bold text-orange-300">{group.stats.rto_initiated}</p><p className="text-xs text-wt-text-muted">RTO Init</p></div>}
                          {group.stats.rto_in_transit > 0 && <div className="text-center"><p className="text-lg font-bold text-orange-400">{group.stats.rto_in_transit}</p><p className="text-xs text-wt-text-muted">RTO IT</p></div>}
                          {group.stats.rto_delivered > 0 && <div className="text-center"><p className="text-lg font-bold text-red-400">{group.stats.rto_delivered}</p><p className="text-xs text-wt-text-muted">RTO Dlv</p></div>}
                          {group.stats.failed > 0 && <div className="text-center"><p className="text-lg font-bold text-red-400">{group.stats.failed}</p><p className="text-xs text-wt-text-muted">Failed</p></div>}
                        </div>
                        {isExpanded ? <ChevronUp className="h-5 w-5 text-wt-text-muted" /> : <ChevronDown className="h-5 w-5 text-wt-text-muted" />}
                      </div>
                    </div>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-t border-wt-border">
                          <div className="p-4 space-y-3">
                            {group.shipments.map(shipment => {
                              const statusCfg = statusConfig[shipment.status] || statusConfig.booked;
                              const StatusIcon = statusCfg.icon;
                              const showNdrActions = ['failed', 'ndr', 'rto'].includes(shipment.status) || shipment.ndr_status;

                              return (
                                <div key={`${shipment.order_id}-${shipment.shipment_number}`} className="p-3 bg-wt-subtle rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className={`p-2 rounded-lg ${statusCfg.bg}`}><StatusIcon className={`h-4 w-4 ${statusCfg.color}`} /></div>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <p className="text-wt-text-primary font-medium">{shipment.order_number}</p>
                                          {shipment.shipment_number > 1 && (
                                            <span className="text-xs bg-wt-gold/20 text-wt-gold px-2 py-0.5 rounded">Shipment {shipment.shipment_number}</span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-wt-text-muted">
                                          <span>AWB: {shipment.awb_number}</span>
                                          <button onClick={(e) => { e.stopPropagation(); copyToClipboard(shipment.awb_number); }} className="hover:text-wt-gold">
                                            <Copy className="h-3 w-3" />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.color}`}>{statusCfg.label}</span>
                                      
                                      {/* Action Buttons */}
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        onClick={(e) => { e.stopPropagation(); syncSingleTracking(shipment); }} 
                                        className="text-wt-text-muted hover:text-wt-gold"
                                        disabled={syncingShipment === shipment.awb_number}
                                        title="Sync Tracking"
                                      >
                                        <RefreshCw className={`h-4 w-4 ${syncingShipment === shipment.awb_number ? 'animate-spin' : ''}`} />
                                      </Button>
                                      
                                      {shipment.tracking_url && (
                                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); window.open(shipment.tracking_url, '_blank'); }} className="text-wt-text-muted hover:text-wt-gold">
                                          <ExternalLink className="h-4 w-4" />
                                        </Button>
                                      )}
                                      
                                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); viewShipmentDetails(shipment); }} className="text-wt-text-muted hover:text-wt-gold">
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>

                                  {/* NDR Actions for failed/ndr shipments */}
                                  {showNdrActions && (
                                    <div className="mt-3 pt-3 border-t border-wt-border flex items-center gap-2">
                                      <span className="text-xs text-orange-400 mr-2">NDR Actions:</span>
                                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setNdrAction('reattempt'); openNdrAction(shipment); }} className="text-xs border-orange-500/50 text-orange-400 hover:bg-orange-500/10">
                                        <RefreshCw className="h-3 w-3 mr-1" />
                                        Reattempt
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setNdrAction('rto'); openNdrAction(shipment); }} className="text-xs border-red-500/50 text-red-400 hover:bg-red-500/10">
                                        <RotateCcw className="h-3 w-3 mr-1" />
                                        RTO
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Order Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-wt-text-primary">{selectedOrder.order_number}</p>
                  <p className="text-sm text-wt-text-muted">{new Date(selectedOrder.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig[selectedOrder.fulfillment_status]?.bg} ${statusConfig[selectedOrder.fulfillment_status]?.color}`}>
                  {statusConfig[selectedOrder.fulfillment_status]?.label}
                </span>
              </div>
              <div className="bg-wt-subtle rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <User className="h-4 w-4 text-wt-text-muted mt-1" />
                  <div><p className="text-sm text-wt-text-muted">Customer</p><p className="text-wt-text-primary">{selectedOrder.customer_name}</p></div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-wt-text-muted mt-1" />
                  <div>
                    <p className="text-sm text-wt-text-muted">Delivery Address</p>
                    <p className="text-wt-text-primary">
                      {typeof selectedOrder.shipping_address === 'object' 
                        ? [selectedOrder.shipping_address?.address1, selectedOrder.shipping_address?.city].filter(Boolean).join(', ')
                        : selectedOrder.shipping_address}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => { setDetailDialogOpen(false); navigate(`/orders?openOrder=${selectedOrder?.id}`); }} className="flex-1 bg-wt-gold hover:bg-wt-gold-hover text-black">View in Orders</Button>
                <Button onClick={() => setDetailDialogOpen(false)} variant="outline" className="border-wt-border text-wt-text-secondary">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Shipment Detail Dialog */}
      <Dialog open={shipmentDetailOpen} onOpenChange={setShipmentDetailOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">Shipment Details</DialogTitle>
          </DialogHeader>
          {selectedShipment && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-wt-text-primary">{selectedShipment.order_number}</p>
                  <p className="text-sm text-wt-text-muted">Shipment #{selectedShipment.shipment_number}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${courierConfig[selectedShipment.courier_provider]?.bg || 'bg-gray-500/10'} ${courierConfig[selectedShipment.courier_provider]?.color || 'text-gray-400'}`}>
                  {courierConfig[selectedShipment.courier_provider]?.name || selectedShipment.courier_provider}
                </span>
              </div>

              <div className="bg-wt-subtle rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-wt-text-muted">AWB Number</span>
                  <div className="flex items-center gap-2">
                    <span className="text-wt-text-primary font-mono">{selectedShipment.awb_number}</span>
                    <button onClick={() => copyToClipboard(selectedShipment.awb_number)} className="text-wt-text-muted hover:text-wt-gold"><Copy className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-wt-text-muted">Status</span>
                  <span className={`${statusConfig[selectedShipment.status]?.color || 'text-gray-400'}`}>{statusConfig[selectedShipment.status]?.label || selectedShipment.status}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-wt-text-muted">Customer</span>
                  <span className="text-wt-text-primary">{selectedShipment.customer_name}</span>
                </div>
                {selectedShipment.cod_amount > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-wt-text-muted">COD Amount</span>
                    <span className="text-wt-gold font-semibold">{formatCurrency(selectedShipment.cod_amount)}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                {selectedShipment.tracking_url && (
                  <Button onClick={() => window.open(selectedShipment.tracking_url, '_blank')} className="flex-1 bg-wt-gold hover:bg-wt-gold-hover text-black">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Track Shipment
                  </Button>
                )}
                <Button onClick={() => { setShipmentDetailOpen(false); openNdrAction(selectedShipment); }} variant="outline" className="border-orange-500/50 text-orange-400">
                  NDR Actions
                </Button>
                <Button onClick={() => setShipmentDetailOpen(false)} variant="outline" className="border-wt-border text-wt-text-secondary">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NDR Action Dialog */}
      <Dialog open={ndrActionOpen} onOpenChange={setNdrActionOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-xl">
              {ndrAction === 'reattempt' ? 'Request Delivery Reattempt' : 'Request RTO (Return to Origin)'}
            </DialogTitle>
          </DialogHeader>
          {selectedShipment && (
            <div className="space-y-4">
              <div className="bg-wt-subtle rounded-lg p-3">
                <p className="text-sm text-wt-text-muted">Shipment</p>
                <p className="text-wt-text-primary font-medium">{selectedShipment.order_number} - AWB: {selectedShipment.awb_number}</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-wt-text-primary">Action Type</Label>
                  <Select value={ndrAction} onValueChange={setNdrAction}>
                    <SelectTrigger className="mt-1 bg-wt-bg border-wt-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-paper border-wt-border">
                      <SelectItem value="reattempt">Reattempt Delivery</SelectItem>
                      <SelectItem value="rto">Return to Origin (RTO)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {ndrAction === 'reattempt' && (
                  <div>
                    <Label className="text-wt-text-primary">Reattempt Date</Label>
                    <DateInput  value={ndrDate} onChange={(e) => setNdrDate(e.target.value)} className="mt-1 bg-wt-bg border-wt-border" min={new Date().toISOString().split('T')[0]} />
                  </div>
                )}

                <div>
                  <Label className="text-wt-text-primary">Reason / Instructions</Label>
                  <Textarea value={ndrReason} onChange={(e) => setNdrReason(e.target.value)} placeholder={ndrAction === 'reattempt' ? 'e.g., Customer requested delivery after 5 PM' : 'e.g., Customer refused delivery'} className="mt-1 bg-wt-bg border-wt-border" rows={3} />
                </div>
              </div>

              <DialogFooter>
                <Button onClick={() => setNdrActionOpen(false)} variant="outline" className="border-wt-border text-wt-text-secondary">Cancel</Button>
                <Button onClick={handleNdrAction} disabled={ndrProcessing} className={ndrAction === 'reattempt' ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}>
                  {ndrProcessing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  {ndrAction === 'reattempt' ? 'Request Reattempt' : 'Request RTO'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveriesPage;
